import { isRateLimited } from '../utils/rateLimit';
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from '../types';
import { authApi, ApiUser } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  // Birey SSO: complete login from an IdP-issued Digipo JWT (no password). Returns
  // the user's role on success (for post-login routing), or null on failure.
  loginWithToken: (token: string) => Promise<string | null>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Marks the CURRENT session as having come from Birey SSO rather than a password.
// Set only in loginWithToken (the IdP callback's only consumer) and read only by
// logout, which needs it to decide whether ending the local session is enough.
const SSO_KEY = 'dijipo_sso';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * End the Birey (IdP) SSO session too — Single Logout, the same shape Sınav.Link uses.
 *
 * ⚠️ WHY LOGOUT CANNOT BE PURELY LOCAL. Clearing `dijipo_token` ends Digipo's own
 * session, but the IdP's session cookie lives on the issuer's origin and is untouched
 * by anything this origin can do. So "Çıkış" followed by "Birey ile Giriş Yap" used to
 * sail straight back into the SAME account with no prompt — /oauth/authorize found a
 * live SSO session and re-issued. The only way out was a different browser or waiting
 * for the cookie to expire.
 *
 * A full-page navigation is required (not fetch/XHR): the cookie is third-party to
 * this origin, so only a top-level request carries it and only the IdP's own response
 * can clear it.
 *
 * Routed through OUR API rather than linking the IdP directly, because the IdP only
 * returns to a REGISTERED redirect origin and that is the API's, not the web app's.
 * The API makes the final hop back to /login. See the route's own note.
 */
function endBireySsoSession(): void {
  window.location.href = `${API_URL}/auth/birey/logout`;
}

function apiUserToUser(u: ApiUser): User {
  // Spread the rich payload first so downstream pages (Profile, Dashboard) can read
  // phone, grade, institution, streaks etc. Then overwrite `name` with the full
  // "first last" form so the dashboard greeting includes the surname.
  return {
    ...(u as any),
    id: u.id,
    name: `${u.name} ${u.surname}`.trim(),
    email: u.email,
    role: u.role,
    surname: u.surname,
    studentCode: u.studentCode ?? undefined,
    avatarUrl: u.avatarUrl ?? undefined,
  } as User;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('dijipo_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('dijipo_token')
  );

  const sessionGeneration = useRef(0);
  const ssoPending = useRef(false);

  const loginWithToken = async (newToken: string): Promise<string | null> => {
    const generation = ++sessionGeneration.current;
    ssoPending.current = true;
    try {
      // Validate the candidate explicitly; concurrent reads retain the established session.
      const { data } = await authApi.me(newToken, () => generation === sessionGeneration.current);
      if (generation !== sessionGeneration.current) return null;
      localStorage.setItem('dijipo_token', newToken);
      setToken(newToken);
      const userData = apiUserToUser(data);
      setUser(userData);
      localStorage.setItem('dijipo_user', JSON.stringify(userData));
      // This session came from the IdP, so logout has an IdP session to end too.
      localStorage.setItem(SSO_KEY, 'birey');
      return userData.role;
    } catch (error) {
      if (generation !== sessionGeneration.current) return null;
      // The callback retains the candidate in memory; a 429 leaves the old session intact.
      if (isRateLimited(error)) throw error;
      localStorage.removeItem('dijipo_token');
      localStorage.removeItem('dijipo_user');
      localStorage.removeItem(SSO_KEY);
      setToken(null);
      return null;
    } finally {
      if (generation === sessionGeneration.current) ssoPending.current = false;
    }
  };

  const logout = () => {
    sessionGeneration.current += 1;
    ssoPending.current = false;
    // Read the marker BEFORE clearing, then clear unconditionally: whatever happens
    // next, this browser must not keep a Digipo session.
    const wasSso = localStorage.getItem(SSO_KEY) === 'birey';
    setUser(null);
    setToken(null);
    localStorage.removeItem('dijipo_user');
    localStorage.removeItem('dijipo_token');
    localStorage.removeItem(SSO_KEY);
    // Password sessions have no IdP cookie, so they stay on the local SPA route the
    // caller navigates to. Only an SSO session needs the round trip — and it is a
    // SINGLE LOGOUT: ending the Birey session also signs this browser out of the
    // other Birey products (Sınav.Link etc.), which is what "Çıkış" means in an SSO
    // estate and what makes re-login prompt for credentials again.
    if (wasSso) endBireySsoSession();
  };

  // Refresh user from /auth/me — picks up any new backend fields
  // (e.g. teacher_subjects, primary_institution added after initial login).
  const refresh = async () => {
    if (!token || ssoPending.current) return;
    const generation = sessionGeneration.current;
    const isCurrent = () => generation === sessionGeneration.current && !ssoPending.current && localStorage.getItem('dijipo_token') === token;
    try {
      const { data } = await authApi.me(token, isCurrent);
      if (!isCurrent()) return;
      const userData = apiUserToUser(data);
      setUser(userData);
      localStorage.setItem('dijipo_user', JSON.stringify(userData));
    } catch {
      // If the token is stale or invalid, fall back silently
    }
  };

  // On app mount, always refresh from /auth/me so the UI picks up any newly
  // added backend fields (e.g. teacher_subjects, primary_institution) without
  // requiring the user to log out and log back in.
  useEffect(() => {
    if (token) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loginWithToken, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
